import { z, ZodError } from "zod";

import { HALLIDAY_PAYMENTS_API_URL } from "@shared/constants";
import { RampName, ChainConfig, Asset, Fiat, Token, AssetDetails } from "@shared/types";

import createAxiosApiClient, {
    PaymentsApiClientError,
    ResponseValidationOptions,
    defaultValidationOptions,
} from "./axios";
import { getQueryParamsFromAssetFilters } from "./utils";
import {
    IQuoteRequest,
    IQuoteResponse,
    IInitialConfirmPaymentRequest,
    IConfirmPaymentResponse,
    IPaymentStatus,
    IPaymentStatusesResults,
    IOnrampSessionRequest,
    IOnrampSessionResult,
    IRequestForPaymentBalances,
    IPaymentBalancesResponse,
    IRequestForPrices,
    IRequestForInputs,
    IRequestForOutputs,
    IRequestForOrgPayments,
    IPaymentsFiltersBase,
    IWithdrawPaymentAuthorizationRequest,
    IWithdrawPaymentAuthorizationResponse,
    IWithdrawPaymentRequestWithStateToken,
    IWithdrawPaymentResponse,
} from "@halliday-lib/api";
import { IPrices } from "@halliday-lib/schema";
import { MethodMetadata, API_METHOD_METADATA_KEY } from "./decorators";

/**
 * Configuration options for the PaymentsApiClient.
 */
export interface PaymentsApiClientOptions {
    /** Your API key for authentication */
    apiKey: string;
    /** Optional base URL for the API. Defaults to HALLIDAY_PAYMENTS_API_URL */
    baseUrl?: string;
    /** Response validation configuration */
    responseValidation?: ResponseValidationOptions;
}

/**
 * Payments API client for interacting with the Halliday Payments API.
 *
 * This client handles API requests including setting the required headers and
 * managing the API key for authentication. It also validates API responses
 * to help detect when the SDK version might be outdated.
 */
class PaymentsApiClient {
    /**
     * Axios API client instance.
     * @private
     */
    private apiClient: ReturnType<typeof createAxiosApiClient>;

    /**
     * Creates an instance of PaymentsAPIClient.
     *
     * @param {PaymentsApiClientOptions} options The configuration options for the client.
     * @param {!string} options.apiKey {string} The API key for authentication.
     * @param {string=} options.baseUrl {string} (optional) The base URL for the API.
     * @param {ResponseValidationOptions=} options.responseValidation {@link ResponseValidationOptions} (optional) Response validation helps checking response data format from Halliday payments API
     * and notifies if changes detected. It is enabled by default, turn it off by:
     * @example
     * { apiKey: "YOUR_API_KEY", responseValidation: { enabled: false } }
     */
    constructor(options: PaymentsApiClientOptions) {
        const { apiKey, baseUrl = HALLIDAY_PAYMENTS_API_URL, responseValidation = {} } = options;

        // Merge response validation options with defaults
        const responseValidationOptions: ResponseValidationOptions = {
            ...defaultValidationOptions,
            ...responseValidation,
        };

        this.apiClient = createAxiosApiClient({
            baseUrl,
            apiKey,
            responseValidationOptions,
        });
    }

    /**
     * Retrieves the supported chain configuration.
     *
     * @param {boolean} sandbox {boolean} Whether to retrieve chains in the sandbox environment.
     * @returns {Promise<Record<string, ChainConfig>>} A promise that resolves to a mapping from network to their {@link ChainConfig chain configurations}.
     * @throws {PaymentsApiClientError}
     *  - {@link PaymentsApiClientError}
     */
    @MethodMetadata({
        description: "Retrieves the supported chain configuration.",
        httpVerb: "GET",
        endpoint: "/chains",
        inputSchema: z.boolean().optional(),
        outputSchema: z.record(z.string(), ChainConfig),
    })
    getChains = async (sandbox: boolean = false): Promise<Record<string, ChainConfig>> => {
        const { data: chainConfigs } = await this.apiClient.get<Record<string, ChainConfig>>(
            `/chains?sandbox=${sandbox ? "true" : "false"}`,
        );
        return chainConfigs;
    };

    /**
     * Retrieves the asset details for all or the specified assets.
     *
     * @param {Asset[]=} assets {{@link Asset}[]} (optional) The assets to retrieve the details for.
     * @param {!boolean} sandbox {boolean} Whether to retrieve testnet assets in the response.
     * @returns {Promise<Record<Asset, AssetDetails>>} A promise that resolves to a mapping from {@link Asset asset} to {@link AssetDetails asset details}.
     * @throws {PaymentsApiClientError}
     *  - {@link PaymentsApiClientError}
     */
    @MethodMetadata({
        description: "Retrieves the asset details for all or the specified assets.",
        httpVerb: "GET",
        endpoint: "/assets",
        inputSchema: z.object({
            assets: z.array(Asset).optional(),
            sandbox: z.boolean().optional(),
        }),
        outputSchema: z.record(Asset, AssetDetails),
    })
    getAssetDetails = async ({
        assets = [],
        sandbox,
    }: {
        assets?: Asset[];
        sandbox: boolean;
    }): Promise<Record<Asset, AssetDetails>> => {
        if (typeof sandbox !== "boolean") {
            throw PaymentsApiClientError.parseValidationError({
                message: "Invalid sandbox value",
            });
        }

        let queryParams = `sandbox=${sandbox}`;
        if (assets) {
            if (!Array.isArray(assets)) {
                throw PaymentsApiClientError.parseValidationError({
                    message: "Invalid assets",
                });
            }
            for (const asset of assets) {
                try {
                    const validAsset = Asset.parse(asset);
                    queryParams += `&assets[]=${validAsset}`;
                } catch (error) {
                    throw PaymentsApiClientError.parseValidationError({
                        message: `Invalid asset: ${asset}`,
                        error: error as ZodError,
                    });
                }
            }
        }

        const response = await this.apiClient.get<Record<Asset, AssetDetails>>(`/assets?${queryParams}`);
        return response.data;
    };

    /**
     * Retrieves the supported inputs for the specified filters.
     *
     * @param {AssetFilters} filters Filters to apply to the inputs.
     * @param {Asset[]=} filters.inputs {{@link Asset}[]} (optional) The input assets to be included in the response.
     * @param {!Asset[]} filters.outputs {{@link Asset}[]} The output assets to retrieve inputs for.
     * @param {!boolean} filters.sandbox {boolean} Whether to retrieve assets in the sandbox environment.
     * @param {RampName[]=} filters.onramps {{@link RampName}[]} (optional) The onramp providers that can be used to accept the inputs.
     * @param {RampName[]=} filters.offramps {{@link RampName}[]} (optional) The offramp providers that can be used to deliver the inputs.
     * @returns {Promise<Record<Asset, { fiats: Fiat[]; tokens: Token[] }>>} A mapping from output {@link Asset assets} to their corresponding input {@link Fiat fiats} and {@link Token tokens}.
     * @throws {PaymentsApiClientError}
     *  - {@link PaymentsApiClientError}
     */
    @MethodMetadata({
        description: "Retrieves the supported inputs for the specified filters.",
        httpVerb: "GET",
        endpoint: "/assets/available-inputs",
        inputSchema: IRequestForInputs,
        outputSchema: z.record(Asset, z.object({ fiats: z.array(Fiat), tokens: z.array(Token) })),
    })
    getInputs = async (filters: IRequestForInputs): Promise<Record<Asset, { fiats: Fiat[]; tokens: Token[] }>> => {
        let queryParams: string;
        try {
            const validAssetFilters = IRequestForInputs.parse(filters);
            queryParams = getQueryParamsFromAssetFilters(validAssetFilters);
        } catch (error) {
            throw PaymentsApiClientError.parseValidationError({
                message: "Invalid asset filters",
                error: error as ZodError | Error,
            });
        }
        const response = await this.apiClient.get<Record<Asset, { fiats: Fiat[]; tokens: Token[] }>>(
            `/assets/available-inputs?${queryParams}`,
        );
        return response.data;
    };

    /**
     * Retrieves the supported outputs for the specified filters.
     *
     * @param {AssetFilters} filters Filters to apply to the outputs.
     * @param {!Asset[]} filters.inputs {{@link Asset}[]} The input assets to retrieve outputs for.
     * @param {Asset[]=} filters.outputs {{@link Asset}[]} (optional) The output assets to be included in the response.
     * @param {!boolean} filters.sandbox {boolean} Whether to retrieve assets in the sandbox environment.
     * @param {RampName[]=} filters.onramps {{@link RampName}[]} (optional) The onramp providers that can be used to accept the inputs.
     * @param {RampName[]=} filters.offramps {{@link RampName}[]} (optional) The offramp providers that can be used to deliver the outputs.
     * @returns {Promise<Record<Asset, { fiats: Fiat[]; tokens: Token[] }>>} A mapping from input {@link Asset assets} to their corresponding output {@link Fiat fiats} and {@link Token tokens}.
     * @throws {PaymentsApiClientError}
     *  - {@link PaymentsApiClientError}
     */
    @MethodMetadata({
        description: "Retrieves the supported outputs for the specified filters.",
        httpVerb: "GET",
        endpoint: "/assets/available-outputs",
        inputSchema: IRequestForOutputs,
        outputSchema: z.record(Asset, z.object({ fiats: z.array(Fiat), tokens: z.array(Token) })),
    })
    getOutputs = async (filters: IRequestForOutputs): Promise<Record<Asset, { fiats: Fiat[]; tokens: Token[] }>> => {
        let queryParams: string;
        try {
            const validAssetFilters = IRequestForOutputs.parse(filters);
            queryParams = getQueryParamsFromAssetFilters(validAssetFilters);
        } catch (error) {
            throw PaymentsApiClientError.parseValidationError({
                message: "Invalid asset filters",
                error: error as ZodError | Error,
            });
        }
        const response = await this.apiClient.get<Record<Asset, { fiats: Fiat[]; tokens: Token[] }>>(
            `/assets/available-outputs?${queryParams}`,
        );
        return response.data;
    };

    /**
     * Retrieves quotes for the given input and output asset.
     *
     * @param {!RequestForQuotes} request {{@link RequestForQuotes}} The request for quotes.
     * @returns {Promise<QuotesResponse>} A promise that resolves to the {@link QuotesResponse quotes}.
     * @throws {PaymentsApiClientError}
     *  - {@link PaymentsApiClientError}
     */
    @MethodMetadata({
        description: "Retrieves quotes for the given input and output asset.",
        httpVerb: "POST",
        endpoint: "/payments/quotes",
        inputSchema: IQuoteRequest,
        outputSchema: IQuoteResponse,
    })
    getQuotes = async (request: IQuoteRequest, signal?: AbortSignal): Promise<IQuoteResponse> => {
        let validatedRequestForQuotes;
        try {
            validatedRequestForQuotes = IQuoteRequest.parse(request);
        } catch (error) {
            throw PaymentsApiClientError.parseValidationError({
                message: "Invalid request for quotes",
                error: error as ZodError,
            });
        }
        const response = await this.apiClient.post<IQuoteResponse>("/payments/quotes", validatedRequestForQuotes, {
            ...(signal !== undefined && { signal }),
        });
        return response.data;
    };

    /**
     * Confirms a quote.
     *
     * @param {!RequestForConfirmQuote} request {{@link RequestForConfirmQuote}} The quote to accept.
     * @returns {Promise<ConfirmQuoteResponse>} A promise that resolves to the {@link ConfirmQuoteResponse accepted quote details}.
     * @throws {PaymentsApiClientError}
     *  - {@link PaymentsApiClientError}
     */
    @MethodMetadata({
        description: "Confirms a quote.",
        httpVerb: "POST",
        endpoint: "/payments/confirm",
        inputSchema: IInitialConfirmPaymentRequest,
        outputSchema: IConfirmPaymentResponse,
    })
    confirmQuote = async (requestForConfirmQuote: IInitialConfirmPaymentRequest): Promise<IConfirmPaymentResponse> => {
        let validatedRequestForConfirmQuote;
        try {
            validatedRequestForConfirmQuote = IInitialConfirmPaymentRequest.parse(requestForConfirmQuote);
        } catch (error) {
            throw PaymentsApiClientError.parseValidationError({
                message: "Invalid request for confirming quote",
                error: error as ZodError,
            });
        }
        const response = await this.apiClient.post<IConfirmPaymentResponse>(
            "/payments/confirm",
            validatedRequestForConfirmQuote,
        );
        return response.data;
    };

    /**
     * Creates a onramp provider session for a given payment.
     *
     * @param {!RequestForOnrampSession} request {{@link RequestForOnrampSession}} The request for the payment session.
     * @returns {Promise<OnrampSessionResponse>} A promise that resolves to the {@link OnrampSessionResponse session}.
     * @throws {PaymentsApiClientError}
     *  - {@link PaymentsApiClientError}
     */
    @MethodMetadata({
        description: "Creates a onramp provider session for a given payment.",
        httpVerb: "POST",
        endpoint: "/onramp",
        inputSchema: IOnrampSessionRequest,
        outputSchema: IOnrampSessionResult,
    })
    createPaymentSession = async (request: IOnrampSessionRequest): Promise<IOnrampSessionResult> => {
        let validatedRequest;
        try {
            validatedRequest = IOnrampSessionRequest.parse(request);
        } catch (error) {
            throw PaymentsApiClientError.parseValidationError({
                message: "Invalid request for onramp session",
                error: error as ZodError,
            });
        }
        const response = await this.apiClient.post<IOnrampSessionResult>(`/onramp`, validatedRequest);
        return response.data;
    };

    /**
     * Retrieves the status of a given payment.
     *
     * @param {!PaymentId} payment_id {{@link PaymentId}} The payment ID of the payment to retrieve the status of.
     * @param {AbortSignal=} signal {{@link https://developer.mozilla.org/en-US/docs/Web/API/AbortSignal AbortSignal}} (optional) Checkout {@link https://developer.mozilla.org/en-US/docs/Web/API/AbortController AbortController} if need to abort the request.
     * @returns {Promise<PaymentStatus | null>} A promise that resolves to {@link PaymentStatus} or null if the request is aborted.
     * @throws {PaymentsApiClientError}
     *  - {@link PaymentsApiClientError}
     */
    @MethodMetadata({
        description: "Retrieves the status of a given payment.",
        httpVerb: "GET",
        endpoint: "/payments",
        inputSchema: z.uuid(),
        outputSchema: IPaymentStatus,
    })
    getStatus = async (paymentId: string, signal?: AbortSignal): Promise<IPaymentStatus | null> => {
        try {
            z.uuid().parse(paymentId);
        } catch (error) {
            throw PaymentsApiClientError.parseValidationError({
                message: "Invalid payment ID",
                error: error as ZodError,
            });
        }
        const searchParams = new URLSearchParams();
        searchParams.set("payment_id", paymentId);
        const searchParamsString = searchParams.toString();

        const response = await this.apiClient.get<IPaymentStatus>(`/payments?${searchParamsString}`, {
            ...(signal !== undefined && { signal }),
        });
        return response.data;
    };

    /**
     * Retrieves the payments history.
     *
     * @param {RequestForPaymentsHistory} request {{@link RequestForPaymentsHistory}} The request for the payments history.
     * @returns {Promise<PaymentHistoryResult>} A promise that resolves to the {@link PaymentHistoryResult history}.
     * @throws {PaymentsApiClientError}
     *  - {@link PaymentsApiClientError}
     */
    @MethodMetadata({
        description: "Retrieves the payments history of a given destination address.",
        httpVerb: "GET",
        endpoint: "/payments/history",
        inputSchema: IRequestForOrgPayments,
        outputSchema: IPaymentStatusesResults,
    })
    getPaymentsHistory = async (request: z.input<typeof IRequestForOrgPayments>): Promise<IPaymentStatusesResults> => {
        // Validate locally without IRequestForOrgPayments transforms (pagination_key transform
        // converts string → object which breaks URL param serialization). Build from
        // IPaymentsFiltersBase because IRequestForOrgPayments carries a superRefine, which
        // disallows .omit(); the base holds every field this method serializes.
        const PaymentsHistoryValidator = IPaymentsFiltersBase.extend({
            pagination_key: z.string().optional(),
        });

        let validatedRequest;
        try {
            validatedRequest = PaymentsHistoryValidator.parse(request);
        } catch (error) {
            throw PaymentsApiClientError.parseValidationError({
                message: "Invalid request for payments",
                error: error as ZodError,
            });
        }
        const queryParams = new URLSearchParams();
        if (validatedRequest.id_query) {
            queryParams.set("id_query", validatedRequest.id_query);
        }
        if (validatedRequest.pagination_key) {
            queryParams.set("pagination_key", validatedRequest.pagination_key);
        }
        if (validatedRequest.dest_address) {
            queryParams.set("dest_address", validatedRequest.dest_address);
        }
        if (validatedRequest.owner_address) {
            queryParams.set("owner_address", validatedRequest.owner_address);
        }
        if (validatedRequest.limit) {
            queryParams.set("limit", validatedRequest.limit.toString());
        }
        if (validatedRequest.category) {
            queryParams.set("category", validatedRequest.category);
        }
        if (validatedRequest.label) {
            queryParams.set("label", validatedRequest.label);
        }
        let queryParamsString = queryParams.toString();
        if (queryParamsString) queryParamsString = `?${queryParamsString}`;
        const response = await this.apiClient.get<IPaymentStatusesResults>(`/payments/history${queryParamsString}`);
        return response.data;
    };

    @MethodMetadata({
        description: "Retrieves the balance of a given workflow or spw address.",
        httpVerb: "POST",
        endpoint: "/payments/balances",
        inputSchema: IRequestForPaymentBalances,
        outputSchema: IPaymentBalancesResponse,
    })
    getBalance = async (request: IRequestForPaymentBalances): Promise<IPaymentBalancesResponse> => {
        let validatedRequest;
        try {
            validatedRequest = IRequestForPaymentBalances.parse(request);
        } catch (error) {
            throw PaymentsApiClientError.parseValidationError({
                message: "Invalid request for balance",
                error: error as ZodError,
            });
        }
        const response = await this.apiClient.post<IPaymentBalancesResponse>(`/payments/balances`, validatedRequest);
        return response.data;
    };

    @MethodMetadata({
        description: "Retrieves the prices tokens in a given fiat currency.",
        httpVerb: "POST",
        endpoint: "/prices",
        inputSchema: IRequestForPrices,
        outputSchema: IPrices,
    })
    getPrices = async (request: IRequestForPrices): Promise<IPrices> => {
        let validatedRequest;
        try {
            validatedRequest = IRequestForPrices.parse(request);
        } catch (error) {
            throw PaymentsApiClientError.parseValidationError({
                message: "Invalid request for prices",
                error: error as ZodError,
            });
        }
        const response = await this.apiClient.post<IPrices>(`/prices`, validatedRequest);
        return response.data;
    };

    /**
     * Retrieves the withdraw payload for a given workflow.
     *
     * @param {RequestForWithdrawAuthorization} request {{@link RequestForWithdrawAuthorization}} The request for the withdraw authorization payload.
     * @returns {Promise<WithdrawAuthorizationResponse>} A promise that resolves to the {@link WithdrawAuthorizationResponse withdraw authorization payload}.
     * @throws {PaymentsApiClientError}
     *  - {@link PaymentsApiClientError}
     */
    @MethodMetadata({
        description: "Retrieves the withdraw authorization for a given workflow.",
        httpVerb: "POST",
        endpoint: "/payments/withdraw",
        inputSchema: IWithdrawPaymentAuthorizationRequest,
        outputSchema: IWithdrawPaymentAuthorizationResponse,
    })
    getWithdrawAuthorization = async (
        request: IWithdrawPaymentAuthorizationRequest,
    ): Promise<IWithdrawPaymentAuthorizationResponse> => {
        let validatedRequest;
        try {
            validatedRequest = IWithdrawPaymentAuthorizationRequest.parse(request);
        } catch (error) {
            throw PaymentsApiClientError.parseValidationError({
                message: "Invalid request for withdraw authorization",
                error: error as ZodError,
            });
        }

        const response = await this.apiClient.post<IWithdrawPaymentAuthorizationResponse>(
            "/payments/withdraw",
            validatedRequest,
        );
        return response.data;
    };

    /**
     * Submits the owner signature for a withdraw payload.
     *
     * @param {RequestForWithdraw} request {{@link RequestForWithdraw}} The request for the withdraw payload.
     * @returns {Promise<WithdrawResponse>} A promise that resolves to the {@link WithdrawResponse transaction ID}.
     * @throws {PaymentsApiClientError}
     *  - {@link PaymentsApiClientError}
     */
    @MethodMetadata({
        description: "Submits the owner signature for a withdraw payload.",
        httpVerb: "POST",
        endpoint: "/payments/withdraw/confirm",
        inputSchema: IWithdrawPaymentRequestWithStateToken,
        outputSchema: IWithdrawPaymentResponse,
    })
    withdraw = async (request: IWithdrawPaymentRequestWithStateToken): Promise<IWithdrawPaymentResponse> => {
        let validatedRequest;
        try {
            validatedRequest = IWithdrawPaymentRequestWithStateToken.parse(request);
        } catch (error) {
            throw PaymentsApiClientError.parseValidationError({
                message: "Invalid request for withdraw",
                error: error as ZodError,
            });
        }

        const response = await this.apiClient.post<IWithdrawPaymentResponse>(
            `/payments/withdraw/confirm`,
            validatedRequest,
        );
        return response.data;
    };

    /**
     * Returns the method metadata for a given method name.
     *
     * @param methodName The name of the method to get the method metadata for.
     * @returns The method metadata for the given method name.
     */
    static toJSON<T extends keyof PaymentsApiClient>(methodName: T): MethodMetadata | undefined {
        return Reflect.getMetadata(API_METHOD_METADATA_KEY, this.prototype, methodName);
    }
}

export default PaymentsApiClient;
